import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";

import heroBgFallback from "@/assets/optimized/hero-bg-fallback.jpg";
import heroBg640 from "@/assets/optimized/hero-bg-640.webp";
import heroBg960 from "@/assets/optimized/hero-bg-960.webp";
import heroBg1280 from "@/assets/optimized/hero-bg-1280.webp";
import heroBg1600 from "@/assets/optimized/hero-bg-1600.webp";

const metrics = [
  { label: "Abgabe ab", value: "0,15 m³" },
  { label: "Verfügbarkeit", value: "6 Tage / Woche" },
];

const HeroSection = () => {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "11%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.86], [1, 0.3]);

  const [isOpen, setIsOpen] = useState<boolean | null>(null);

  useEffect(() => {
    const updateOpenState = () => {
      const now = new Date();
      const day = now.getDay();
      const hour = now.getHours();
      setIsOpen(day >= 1 && day <= 6 && hour >= 6 && hour < 20);
    };

    updateOpenState();

    const interval = window.setInterval(updateOpenState, 60_000);
    return () => window.clearInterval(interval);
  }, []);

  const statusText =
    isOpen === null ? "Mo–Sa · 06:00 bis 20:00 Uhr" : isOpen ? "Jetzt geöffnet" : "Derzeit geschlossen";

  return (
    <section
      ref={ref}
      className="relative flex min-h-[100svh] overflow-hidden pt-24 md:pt-28"
    >
      <picture className="absolute inset-[-4%] block overflow-hidden">
        <source
          type="image/webp"
          srcSet={`${heroBg640} 640w, ${heroBg960} 960w, ${heroBg1280} 1280w, ${heroBg1600} 1600w`}
          sizes="100vw"
        />
        <motion.img
          src={heroBgFallback}
          alt=""
          aria-hidden="true"
          width={1920}
          height={1068}
          loading="eager"
          decoding="async"
          className="h-full w-full object-cover"
          style={{ y: bgY, scale: bgScale }}
        />
      </picture>
      <div className="absolute inset-0 bg-[linear-gradient(108deg,hsl(222_25%_8%/0.94)_12%,hsl(222_25%_8%/0.8)_46%,hsl(222_25%_8%/0.6)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,hsl(206_90%_58%/0.18),transparent_28%),radial-gradient(circle_at_82%_14%,hsl(206_90%_58%/0.12),transparent_24%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:82px_82px] [mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.75),transparent)] opacity-40" />
      <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-background via-background/75 to-transparent" />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="container relative z-10 mx-auto flex min-h-[calc(100svh-6rem)] items-end px-4 pb-10 pt-8 md:pb-14"
      >
        <div className="w-full">
          <div className="grid gap-14 lg:grid-cols-[minmax(0,1.1fr)_320px] lg:items-end">
            <div className="max-w-3xl">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mb-6 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2"
              >
                <span className="info-label text-primary">Beton2Go by Romano</span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span
                  className={`text-xs uppercase tracking-[0.18em] ${
                    isOpen === null
                      ? "text-primary/95"
                      : isOpen
                        ? "text-emerald-300"
                        : "text-rose-300"
                  }`}
                >
                  {statusText}
                </span>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.08 }}
                className="mb-5 max-w-xl font-display text-[13px] uppercase tracking-[0.32em] text-primary/95"
              >
                Frischbeton direkt zum Mitnehmen in Nalbach
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 24, filter: "blur(12px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ duration: 1.1, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-4xl text-[clamp(3.6rem,10vw,8rem)] font-bold uppercase leading-[0.88] tracking-[0.02em] text-foreground"
                style={{ fontFamily: "var(--font-display-strong)" }}
              >
                Beton.
                <br />
                Ohne Umweg.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.24, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 max-w-[38rem] text-base leading-relaxed text-muted-foreground md:text-lg"
              >
                Für Profis, Gartenbauer und private Baustellen: holen Sie exakt die Menge Frischbeton,
                die Sie wirklich brauchen. Schnell geladen und klar kalkulierbar.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.34, ease: [0.16, 1, 0.3, 1] }}
                className="mt-10 flex flex-col gap-4 sm:flex-row"
              >
                <Link
                  to="/funktion"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-primary bg-primary px-7 py-4 font-display text-sm uppercase tracking-[0.16em] text-primary-foreground transition-all duration-300 hover:translate-y-[-1px] hover:bg-primary/90"
                >
                  Ablauf ansehen
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>

                <Link
                  to="/#preise"
                  className="inline-flex items-center justify-center rounded-full border border-white/14 bg-white/[0.03] px-7 py-4 font-display text-sm uppercase tracking-[0.16em] text-foreground transition-colors duration-300 hover:bg-white/[0.08]"
                >
                  Preise prüfen
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.28 }}
              className="surface-panel-strong hidden self-end p-6 lg:block"
            >
              <p className="info-label">Standort</p>
              <p className="mt-3 font-display text-2xl uppercase leading-none text-foreground">
                Primsaue 5
              </p>
              <p className="mt-2 text-sm text-muted-foreground">66809 Nalbach · Deutschland</p>

              <div className="mt-7 space-y-5 border-t border-white/10 pt-6">
                <div>
                  <p className="info-label">Öffnungszeiten</p>
                  <p className="mt-2 text-lg text-foreground">Mo–Sa · 06:00 bis 20:00 Uhr</p>
                </div>
                <div>
                  <p className="info-label">Ablauf</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Ohne Vorbestellung direkt vor Ort auswählen, laden und weiterfahren.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.45 }}
            className="mt-12 grid gap-5 border-t border-white/10 pt-6 md:grid-cols-2 md:gap-8"
          >
            {metrics.map((metric) => (
              <div key={metric.label} className="flex items-start justify-between gap-5">
                <div>
                  <p className="info-label">{metric.label}</p>
                  <p className="mt-2 font-display text-2xl uppercase tracking-[0.04em] text-foreground">
                    {metric.value}
                  </p>
                </div>
                <div className="mt-1 h-9 w-px bg-white/10" />
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className="pointer-events-none absolute bottom-3 right-4 z-10 hidden flex-col items-center gap-1.5 xl:flex"
        animate={{ y: [0, 7, 0] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
      >
        <span className="info-label">Weiter nach unten</span>
        <ChevronDown className="h-4 w-4 text-primary" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
